import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "../../../Component/RetailerMainComponents/Badge";

export default function InvoiceMobileCard({
  row,
  tab,
  money,
  onPayNow,
  formatDateTime,
  onViewInvoice,
  onViewUninvoiced,
}) {

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

  const isRetailerMobile =
    typeof window !== "undefined" &&
    window.innerWidth < 640 &&
    Number(authUser?.role) === 5;

  const [expanded, setExpanded] = useState(false);

  /* ---------- STATUS LABEL ---------- */
  const displayStatus =
    tab === "creditNotes"
      ? ""
      : row.invoiceStatus === "paid"
      ? "Paid"
      : row.invoiceStatus === "partially_paid"
      ? "Partially Paid"
      : "Unpaid";

  /* ---------- BADGE COLOR ---------- */
  const badgeVariant =
    displayStatus === "Paid"
      ? "success"
      : displayStatus === "Partially Paid"
      ? "warning"
      : "danger";

  /* ---------- LEFT BORDER ACCENT ---------- */
  const borderAccent =
    displayStatus === "Paid"
      ? "border-l-emerald-500"
      : displayStatus === "Partially Paid"
      ? "border-l-amber-500"
      : "border-l-rose-500";

  return (
    <div
      className={`
        rounded-2xl overflow-hidden border-l-4 mt-5
        ${borderAccent}
        ${
          isRetailerMobile
            ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)] text-white"
            : "border border-slate-200 bg-white shadow-sm"
        }
      `}
    >
      {/* ================= HEADER ================= */}
      <div className="flex items-start justify-between px-4 pt-4">
        <p className="text-sm font-semibold text-slate-900">
          {row.customerName || "—"}
        </p>

        <Badge variant={badgeVariant}>{displayStatus}</Badge>
      </div>

      {/* ================= CONTENT ================= */}
      <div className="mt-3 grid grid-cols-2 gap-3 px-4 text-xs">
        {/* ---------- INVOICED ---------- */}
        {tab === "invoiced" && (
          <>
            <div>
              <p className="text-slate-400">Invoice</p>
              <p className="font-semibold text-slate-800">
                {row.invoice_Code || "—"}
              </p>
            </div>

            <div>
              <p className="text-slate-400">Customer Code</p>
              <p className="font-semibold text-slate-800">
                {row.customerCode || "—"}
              </p>
            </div>

            <div>
              <p className="text-slate-400">Amount</p>
              <p className="font-bold text-blue-600">
                {money(row.amount)}
              </p>
            </div>

            <div>
              <p className="text-slate-400">Invoice Date</p>
              <p className="font-semibold text-slate-800">
                {formatDateTime(row.date)}
              </p>
            </div>
          </>
        )}

        {/* ---------- UNINVOICED ---------- */}
        {tab === "uninvoiced" && (
          <>
            <div>
              <p className="text-slate-400">Warranty</p>
              <p className="font-semibold text-slate-800">
                {row.warrantyId || "—"}
              </p>
            </div>

            <div>
              <p className="text-slate-400">Amount</p>
              <p className="font-bold text-blue-600">
                {money(row.amount)}
              </p>
            </div>

            <div className="col-span-2">
              <p className="text-slate-400">Purchase Date</p>
              <p className="font-semibold text-slate-800">
                {formatDateTime(row.purchaseDate)}
              </p>
            </div>
          </>
        )}

        {/* ---------- CREDIT NOTES ---------- */}
        {tab === "creditNotes" && (
          <>
            <div>
              <p className="text-slate-400">Credit Note</p>
              <p className="font-semibold text-slate-800">
                {row.creditId || "—"}
              </p>
            </div>

            <div>
              <p className="text-slate-400">Invoice</p>
              <p className="font-semibold text-slate-800">
                {row.invoiceId || "—"}
              </p>
            </div>

            <div>
              <p className="text-slate-400">Amount</p>
              <p className="font-bold text-blue-600">
                {money(row.amount)}
              </p>
            </div>

            <div>
              <p className="text-slate-400">Date</p>
              <p className="font-semibold text-slate-800">
                {formatDateTime(row.date)}
              </p>
            </div>
          </>
        )}

      </div>

      {/* ================= EXPAND (INVOICED ONLY) ================= */}
      {tab === "invoiced" && (
        <button
          onClick={() => setExpanded((p) => !p)}
          className="mt-3 flex w-full items-center justify-center gap-1 text-xs font-semibold text-blue-600"
        >
          {expanded ? "View Less" : "View More"}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      )}

      {expanded && tab === "invoiced" && (
        <div className="border-t bg-slate-50 px-4 py-3 text-xs space-y-2">
          <div className="flex justify-between">
            <span className="text-slate-500">Paid</span>
            <span className="font-medium">
              {money(row.paidAmount)}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-slate-500">Credit Note</span>
            <span className="font-medium">
              {money(row.creditNote)}
            </span>
          </div>
        </div>
      )}

      {/* ================= ACTION ================= */}
      <div className="flex gap-2 border-t px-4 py-3">

        { tab === "invoiced" && row.invoiceStatus !== "paid" && (
          <button
            onClick={() => onPayNow(row)}
            className="w-full rounded-xl bg-emerald-600 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            Pay Now
          </button>
        )}

        <button
          onClick={() =>
            tab === "uninvoiced"
              ? onViewUninvoiced(row)
              : onViewInvoice(row)
          }
          className="w-full rounded-xl bg-blue-600 py-2 text-sm font-semibold text-white"
        >
          {tab === "uninvoiced"
            ? "View Details"
            : tab === "creditNotes"
            ? "View Invoice"
            : "View Invoice"}
        </button>

      </div>
    </div>
  );
}

